import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { filter } from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { setTranslocoLang } from 'src/app/utils/translocoLang';

type TEditLanguageFormGroup = FormGroup<{
  language: FormControl<string | null>;
}>;

@Component({
  selector: 'app-edit-settings',
  templateUrl: './edit-settings.component.html',
  styleUrls: ['./edit-settings.component.scss'],
})
/**
 * Edit settings form component
 */
export class EditSettingsComponent implements OnInit {
  editLanguageFormGroup!: TEditLanguageFormGroup;
  loading: boolean = false;

  /**
   * @costructor
   */
  constructor(
    private readonly userService: UserService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly translocoService: TranslocoService,
    private readonly snackbarService: SnackBarService
  ) {}

  /**
   * Sets up edit account form group
   */
  ngOnInit() {
    this.editLanguageFormGroup = new FormGroup({
      language: new FormControl<string | null>(null),
    });

    this.authenticatedUserService.admin$
      .pipe(filter((admin) => !!admin))
      .subscribe((admin) => {
        this.editLanguageFormGroup?.controls['language'].setValue(
          admin?.language || null
        );
      });
  }

  /**
   * Returns language FormControl from FormGroup
   * @return {FormControl}
   */
  @Input()
  get languageControl() {
    return this.editLanguageFormGroup.controls['language'];
  }

  /**
   * Saves settings changes
   */
  saveSettingsChanges() {
    const lang = this.languageControl.value;
    if (['en', 'pt', 'es'].includes(lang || '')) {
      this.loading = true;
      this.userService.changeLanguage(lang as any).subscribe({
        next: (administrator) => {
          this.loading = false;
          this.snackbarService.success(
            this.translocoService.translate('success')
          );
          const translocoLang = setTranslocoLang(administrator.language!);
          this.translocoService.setActiveLang(translocoLang);
          this.authenticatedUserService.admin = administrator;
        },
        error: () => {
          this.loading = false;
          this.snackbarService.error(this.translocoService.translate('error'));
        },
        complete: () => {
          this.snackbarService.success(
            this.translocoService.translate('success')
          );
        },
      });
    }
  }

  /**
   * The form must be disabled if there's no changes in the data
   * @return {boolean}
   */
  isFormDisabled() {
    return (
      this.editLanguageFormGroup.get('language')?.value ===
      this.authenticatedUserService.admin?.language
    );
  }
}
